import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../components/ui";
import { splitReservationLotNo } from "../../utils/reservationLot";
import {
  listPaperReservations,
  cancelPaperReservation,
  updatePaperReservation,
} from "../../api/paperReservations";
import { listCards } from "../../api/cards";
import { listSalesOrders, getSalesOrder } from "../../api/salesOrders";
import { itemVariantSummary, fmtItemQty } from "../../utils/salesOrderItems";

const CUSTOMERS_PER_DROPDOWN_PAGE = 25;

function fmtDate(d) {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

// Ortak yardımcı; sipariş kalemi dropdown'ları üç ekranda da aynı biçimi kullanıyor.
const fmtQty = fmtItemQty;

export default function PaperReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("active");
  const [filterSalesOrderNo, setFilterSalesOrderNo] = useState("");
  const [filterWorkOrderNo, setFilterWorkOrderNo] = useState("");
  const [filterLotNo, setFilterLotNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editDialog, setEditDialog] = useState(null);

  const loadList = useCallback(async () => {
    const hasFilter = Boolean(
      filterSalesOrderNo.trim() || filterWorkOrderNo.trim() || filterLotNo.trim()
    );
    if (!hasFilter) {
      setReservations([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await listPaperReservations({
        status: statusFilter || undefined,
        sales_order_no: filterSalesOrderNo.trim() || undefined,
        work_order_no: filterWorkOrderNo.trim() || undefined,
        lot_no: filterLotNo.trim() || undefined,
        page,
        limit: 50,
      });
      setReservations(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e?.response?.data?.error?.message || "Yüklenemedi");
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, filterSalesOrderNo, filterWorkOrderNo, filterLotNo, page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Filtre değişince ilk sayfaya dön (stok envanteri filtresiyle aynı davranış).
  useEffect(() => {
    setPage(1);
  }, [filterSalesOrderNo, filterWorkOrderNo, filterLotNo]);

  async function handleCancel(id) {
    if (!window.confirm("Bu rezervasyonu iptal etmek istediğinize emin misiniz?")) return;
    try {
      await cancelPaperReservation(id);
      loadList();
    } catch (e) {
      alert(e?.response?.data?.error?.message || "İptal başarısız");
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <Stack spacing={2}>
      <PageHeader title="Kağıt Rezervasyonları" subtitle="Kağıt partilerini sipariş kalemlerine rezerve edin." />

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label="Aktif" color={statusFilter === "active" ? "primary" : "default"} variant={statusFilter === "active" ? "filled" : "outlined"} onClick={() => { setStatusFilter("active"); setPage(1); }} />
          <Chip label="İptal" color={statusFilter === "cancelled" ? "error" : "default"} variant={statusFilter === "cancelled" ? "filled" : "outlined"} onClick={() => { setStatusFilter("cancelled"); setPage(1); }} />
          <Chip label="Tümü" color={!statusFilter ? "info" : "default"} variant={!statusFilter ? "filled" : "outlined"} onClick={() => { setStatusFilter(""); setPage(1); }} />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Sipariş No"
          value={filterSalesOrderNo}
          onChange={(e) => setFilterSalesOrderNo(e.target.value)}
        />
        <TextField
          size="small"
          label="İş Emri No"
          value={filterWorkOrderNo}
          onChange={(e) => setFilterWorkOrderNo(e.target.value)}
        />
        <TextField
          size="small"
          label="Parti No"
          value={filterLotNo}
          onChange={(e) => setFilterLotNo(e.target.value)}
        />
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Parti No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rez No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Malzeme</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Rez. Miktar</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sipariş No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>İş Emri No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rezerve Eden</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tarih</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Durum</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Kayıt bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {reservations.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.source_lot_no}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {splitReservationLotNo(r.reserved_lot_no).reservationNo || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {r.material_code || ""}{r.material_name ? ` — ${r.material_name}` : ""}
                  </Typography>
                  {r.variant_display_name && (
                    <Typography variant="caption" color="text.secondary">{r.variant_display_name}</Typography>
                  )}
                </TableCell>
                <TableCell align="right">{fmtQty(r.reserved_qty)} {r.material_unit || ""}</TableCell>
                <TableCell>{r.sales_order_no || r.sales_order_id}</TableCell>
                <TableCell>{r.work_order_no || "—"}</TableCell>
                <TableCell>{r.created_by_username || "-"}</TableCell>
                <TableCell>{r.cancelled_at ? fmtDate(r.cancelled_at) : fmtDate(r.created_at)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.status === "active" ? "Aktif" : "İptal"}
                    color={r.status === "active" ? "success" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  {r.status === "active" ? (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Düzenle">
                        <IconButton size="small" onClick={() => setEditDialog(r)}>
                          <span style={{ fontSize: 16 }}>&#9998;</span>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="İptal Et">
                        <IconButton size="small" color="error" onClick={() => handleCancel(r.id)}>
                          <span style={{ fontSize: 16 }}>&times;</span>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Önceki</Button>
          <Typography variant="body2" color="text.secondary">Sayfa {page} / {totalPages}</Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Sonraki</Button>
        </Stack>
      )}

      {editDialog && (
        <EditReservationDialog
          reservation={editDialog}
          onClose={() => setEditDialog(null)}
          onSuccess={() => { setEditDialog(null); loadList(); }}
        />
      )}
    </Stack>
  );
}

function EditReservationDialog({ reservation, onClose, onSuccess }) {
  const [qty, setQty] = useState(String(Number(reservation.reserved_qty)));

  const [customers, setCustomers] = useState([]);
  const [forCustomerCardId, setForCustomerCardId] = useState("");
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerDropdownPage, setCustomerDropdownPage] = useState(0);
  const [customerDropdownAnchor, setCustomerDropdownAnchor] = useState(null);

  const [orderSearch, setOrderSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const orderReq = useRef(0);
  const [forSalesOrderId, setForSalesOrderId] = useState(String(reservation.sales_order_id || ""));

  const [orderItems, setOrderItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [forSalesOrderItemId, setForSalesOrderItemId] = useState(String(reservation.sales_order_item_id || ""));

  const [saving, setSaving] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    listCards({ type: "customer", limit: 500 })
      .then((items) => {
        setCustomers(items);
        if (!initializedRef.current && reservation.sales_order_id) {
          listSalesOrders({ status: "open", limit: 200, page: 1 })
            .then((ords) => {
              const arr = Array.isArray(ords) ? ords : [];
              const match = arr.find((o) => Number(o.id) === Number(reservation.sales_order_id));
              if (match && match.customer_card_id) {
                setForCustomerCardId(String(match.customer_card_id));
              }
            })
            .catch(() => {});
          initializedRef.current = true;
        }
      })
      .catch(() => setCustomers([]));
  }, [reservation.sales_order_id]);

  useEffect(() => {
    let cancelled = false;
    const reqId = ++orderReq.current;
    async function loadOrders() {
      setOrders([]);
      const custId = forCustomerCardId ? Number(forCustomerCardId) : null;
      if (!custId || !Number.isFinite(custId)) return;
      const q = String(orderSearch || "").trim();
      setOrdersLoading(true);
      try {
        const items = await listSalesOrders({ status: "open", search: q || undefined, limit: 100, page: 1 });
        if (cancelled || orderReq.current !== reqId) return;
        const rows = Array.isArray(items) ? items : [];
        setOrders(rows.filter((o) => Number(o.customer_card_id) === Number(custId)));
      } catch {
        if (!cancelled && orderReq.current === reqId) setOrders([]);
      } finally {
        if (!cancelled && orderReq.current === reqId) setOrdersLoading(false);
      }
    }
    const t = setTimeout(loadOrders, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [forCustomerCardId, orderSearch]);

  useEffect(() => {
    let cancelled = false;
    async function loadItems() {
      setOrderItems([]);
      const soId = forSalesOrderId ? Number(forSalesOrderId) : null;
      if (!soId || !Number.isFinite(soId)) return;
      setItemsLoading(true);
      try {
        const d = await getSalesOrder(soId);
        if (cancelled) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setOrderItems(items.filter((it) => String(it.status || "active") === "active"));
      } catch {
        if (!cancelled) setOrderItems([]);
      } finally {
        if (!cancelled) setItemsLoading(false);
      }
    }
    loadItems();
    return () => { cancelled = true; };
  }, [forSalesOrderId]);

  async function handleSave() {
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) { alert("Miktar pozitif olmalı"); return; }
    if (!forSalesOrderId) { alert("Sipariş seçin"); return; }
    if (!forSalesOrderItemId) { alert("Sipariş kalemi seçin"); return; }

    setSaving(true);
    try {
      await updatePaperReservation(reservation.id, {
        qty: q,
        salesOrderId: Number(forSalesOrderId),
        salesOrderItemId: Number(forSalesOrderItemId),
      });
      onSuccess();
    } catch (e) {
      alert(e?.response?.data?.error?.message || "Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Rezervasyon Düzenle #{reservation.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField size="small" label="Kaynak Parti" value={reservation.source_lot_no} disabled sx={{ flex: 1 }} />
            <TextField size="small" label="Rez No" value={splitReservationLotNo(reservation.reserved_lot_no).reservationNo || ""} disabled sx={{ flex: 1 }} />
          </Stack>

          <TextField
            size="small"
            label="Miktar"
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            inputProps={{ min: 0, step: "any" }}
            fullWidth
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, pt: 1 }}>Sipariş Bilgisi</Typography>

          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Box sx={{ minWidth: 320, flex: "1 1 320px" }}>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary", fontSize: "0.75rem" }}>
                Müşteri <Box component="span" sx={{ color: "error.main" }}>*</Box>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  setCustomerDropdownAnchor(e.currentTarget);
                  setCustomerDropdownOpen(true);
                  setCustomerDropdownPage(0);
                }}
                sx={{
                  justifyContent: "flex-start", textAlign: "left", textTransform: "none",
                  height: 40, width: "100%",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                  "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)", bgcolor: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                {forCustomerCardId
                  ? (() => {
                      const c = customers.find((x) => String(x.id) === String(forCustomerCardId));
                      return c ? `${c.code ? `${c.code} - ` : ""}${c.name}` : "Seçiniz";
                    })()
                  : "Seçiniz"}
              </Button>
              <Popover
                open={customerDropdownOpen}
                anchorEl={customerDropdownAnchor}
                onClose={() => setCustomerDropdownOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 340 } } }}
              >
                <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                  <ListItemButton
                    selected={!forCustomerCardId}
                    onClick={() => {
                      setForCustomerCardId("");
                      setOrderSearch("");
                      setForSalesOrderId("");
                      setForSalesOrderItemId("");
                      setOrders([]);
                      setOrderItems([]);
                      setCustomerDropdownOpen(false);
                    }}
                  >
                    <ListItemText primary="Seçiniz" primaryTypographyProps={{ fontStyle: "italic" }} />
                  </ListItemButton>
                  {customers
                    .slice(
                      customerDropdownPage * CUSTOMERS_PER_DROPDOWN_PAGE,
                      (customerDropdownPage + 1) * CUSTOMERS_PER_DROPDOWN_PAGE
                    )
                    .map((c) => (
                      <ListItemButton
                        key={c.id}
                        selected={String(forCustomerCardId) === String(c.id)}
                        onClick={() => {
                          setForCustomerCardId(String(c.id));
                          setOrderSearch("");
                          setForSalesOrderId("");
                          setForSalesOrderItemId("");
                          setOrders([]);
                          setOrderItems([]);
                          setCustomerDropdownOpen(false);
                        }}
                      >
                        <ListItemText primary={(c.code ? `${c.code} - ` : "") + c.name} />
                      </ListItemButton>
                    ))}
                </Box>
                {customers.length > CUSTOMERS_PER_DROPDOWN_PAGE ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1, borderTop: 1, borderColor: "divider" }}>
                    <Button size="small" disabled={customerDropdownPage === 0} onClick={() => setCustomerDropdownPage((p) => p - 1)}>Önceki</Button>
                    <Typography variant="body2" color="text.secondary">
                      Sayfa {customerDropdownPage + 1} / {Math.ceil(customers.length / CUSTOMERS_PER_DROPDOWN_PAGE) || 1}
                    </Typography>
                    <Button
                      size="small"
                      disabled={customerDropdownPage >= Math.ceil(customers.length / CUSTOMERS_PER_DROPDOWN_PAGE) - 1}
                      onClick={() => setCustomerDropdownPage((p) => p + 1)}
                    >Sonraki</Button>
                  </Box>
                ) : null}
              </Popover>
            </Box>

            <TextField
              size="small"
              label="Sipariş Ara (ops.)"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              disabled={!forCustomerCardId}
              sx={{ minWidth: 260, flex: "1 1 260px" }}
            />

            <FormControl
              fullWidth size="small" required
              disabled={!forCustomerCardId || ordersLoading}
              sx={{ minWidth: 260, flex: "1 1 260px" }}
            >
              <InputLabel id="so-label-edit">Sipariş</InputLabel>
              <Select
                labelId="so-label-edit"
                label="Sipariş"
                value={forSalesOrderId}
                onChange={(e) => {
                  setForSalesOrderId(String(e.target.value));
                  setForSalesOrderItemId("");
                }}
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {orders.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>{o.order_no}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControl fullWidth size="small" required disabled={!forSalesOrderId || itemsLoading}>
            <InputLabel id="soi-label-edit">Sipariş Kalemi</InputLabel>
            <Select
              labelId="soi-label-edit"
              label="Sipariş Kalemi"
              value={forSalesOrderItemId}
              onChange={(e) => setForSalesOrderItemId(String(e.target.value))}
              // Menüde iki satır gösteriliyor; kapalı haldeki alan tek satır kalsın.
              renderValue={(val) => {
                if (!val) return <em>Seçiniz</em>;
                const it = orderItems.find((x) => String(x.id) === String(val));
                if (!it) return "";
                const v = itemVariantSummary(it);
                return `#${it.line_no} — ${it.material_code || ""} — ${fmtQty(it.qty)}${v ? ` • ${v}` : ""}`;
              }}
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              {orderItems.map((it) => {
                const v = itemVariantSummary(it);
                return (
                  <MenuItem key={it.id} value={String(it.id)}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{it.line_no} — {it.material_code || ""} {it.material_name || ""} — {fmtQty(it.qty)}
                      </Typography>
                      {v ? (
                        <Typography variant="caption" color="text.secondary">
                          {v}
                        </Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>İptal</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : "Güncelle"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
